/**
 * Compose feature event payloads
 */

import type { IComposeBasePayload } from './composeLifecyclePayloads';
import type { MailboxInfo } from 'owa-client-types';

/**
 * Attachment change types
 */
export type AttachmentChangeType = 'added' | 'removed' | undefined;

/**
 * Attachment types enum
 * Order matters
 */
export enum AddinsSupportedAttachmentType {
    File = 0,
    Item = 1,
    Cloud = 2,
    Base64 = 3,
}

/**
 * Attachment details interface
 */
export interface AttachmentDetails {
    id: string;
    name: string;
    contentType?: string;
    size?: number;
    attachmentType: AddinsSupportedAttachmentType;
    isInline: boolean;
    url?: string;
    base64file?: string;
    contentId?: string;
    isServiceAccessible?: boolean;
}

/**
 * OnMessageAttachmentsChanged event payload
 */
export interface IOnMessageAttachmentsChangedPayload extends IComposeBasePayload {
    /** Type of change */
    attachmentStatus: AttachmentChangeType;
    /** Details about the attachment */
    attachmentDetails: AttachmentDetails;
}

/**
 * Picture change types
 */
export type PictureChangeType = 'added' | 'removed';

/**
 * OnPicturesChanged event payload
 */
export interface IOnPicturesChangedPayload extends IComposeBasePayload {
    /** Type of change */
    changeType: PictureChangeType;
    /** Identifiers of the pictures that were added or removed */
    pictureIds: string[];
}

/**
 * OnMessageFromChanged event payload
 */
export interface IOnMessageFromChangedPayload extends IComposeBasePayload {
    /** Previous from identity (optional — addins retrieve the current value via Office.js APIs) */
    oldFrom?: MailboxInfo;
    /** New from identity (optional — addins retrieve the current value via Office.js APIs) */
    newFrom?: MailboxInfo;
}

/**
 * OnMessageSend event payload (blocking event)
 * This interface intentionally has no additional members beyond IComposeBasePayload
 */
export interface IOnMessageSendPayload extends IComposeBasePayload {
    delaySend?: boolean; // Optional property to indicate if the delay send setting is enabled. Remove if not needed, added to avoid empty interface error.
}

/**
 * Notify SDK that message recipients changed
 * Called by OWA when user adds/removes recipients in compose
 */
export interface IOnRecipientChangedPayload extends IComposeBasePayload {
    /** List each recipient well to show if it is changed */
    to: boolean;
    cc: boolean;
    bcc: boolean;
}
